import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Save, X, DollarSign } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { AdminRoute } from '../../components/admin/AdminRoute';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { supabase } from '../../lib/supabase';

interface SaaSSubscription {
  id: string;
  company_id: string;
  company_name: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string;
  plan_type: 'basic' | 'pro' | 'enterprise';
  status: string;
  billing_cycle: 'monthly' | 'annual';
  price: number;
  discount_percent: number;
  discount_amount: number;
  billing_start_date: string;
  billing_end_date: string | null;
  trial_end_date: string | null;
  delayed_billing_months: number;
  created_at: string;
  updated_at: string;
}

interface SubscriptionWithCompany {
  id: string;
  company_id: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string;
  plan_type: 'basic' | 'pro' | 'enterprise';
  status: string;
  billing_cycle: 'monthly' | 'annual';
  price: number;
  discount_percent: number;
  discount_amount: number;
  billing_start_date: string;
  billing_end_date: string | null;
  trial_end_date: string | null;
  delayed_billing_months: number;
  created_at: string;
  updated_at: string;
  companies: {
    id: string;
    name: string;
  };
}

interface PlatformCoupon {
  id: string;
  name: string;
  code: string;
  coupon_type: 'percent' | 'amount' | 'free_months' | 'referral_credit';
  percent_off: number | null;
  amount_off_cents: number | null;
  free_months: number | null;
  stripe_coupon_id: string | null;
}

interface PlatformPrice {
  id: string;
  name: string;
  description: string | null;
  amount_cents: number;
  interval: 'month' | 'year';
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  is_active: boolean;
  coupons: PlatformCoupon[];
  rules: PlatformPriceRule[];
  feature_sets: PlatformFeatureSet[];
}

interface PlatformPriceRule {
  id: string;
  metric: 'flat' | 'per_location' | 'per_member';
  amount_cents: number;
  interval: 'month' | 'year';
  max_members: number | null;
  max_locations: number | null;
  description: string | null;
  is_active?: boolean;
}

interface PlatformFeatureSet {
  id: string;
  name: string;
  description: string | null;
  features: Record<string, unknown>;
}

export default function RollOpsSubscriptionsPage() {
  const [searchParams] = useSearchParams();
  const companyIdFilter = searchParams.get('companyId');
  const { adminUser, isLoading: authLoading } = useAdminAuth();
  const [subscriptions, setSubscriptions] = useState<SaaSSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<SaaSSubscription | null>(null);
  const [formData, setFormData] = useState({
    company_id: companyIdFilter || '',
    plan_type: 'basic' as 'basic' | 'pro' | 'enterprise',
    billing_cycle: 'monthly' as 'monthly' | 'annual',
    price: 20,
    discount_percent: 0,
    discount_amount: 0,
    delayed_billing_months: 0,
    billing_start_date: new Date().toISOString().split('T')[0],
  });
  const [platformPrices, setPlatformPrices] = useState<PlatformPrice[]>([]);
  const [platformCoupons, setPlatformCoupons] = useState<PlatformCoupon[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [priceForm, setPriceForm] = useState({
    name: '',
    amount_cents: 2000,
    interval: 'month' as 'month' | 'year',
    description: '',
  });
  const [couponForm, setCouponForm] = useState({
    name: '',
    code: '',
    coupon_type: 'free_months' as PlatformCoupon['coupon_type'],
    percent_off: 0,
    amount_off_cents: 0,
    free_months: 1,
    platform_price_id: '',
  });
  const [offerForm, setOfferForm] = useState({
    company_id: '',
    platform_price_id: '',
    notes: '',
  });
  const [ruleForm, setRuleForm] = useState({
    platform_price_id: '',
    metric: 'per_location' as PlatformPriceRule['metric'],
    amount_cents: 2000,
    interval: 'month' as 'month' | 'year',
    max_members: 0,
    max_locations: 0,
    description: '',
  });
  const [featureSetForm, setFeatureSetForm] = useState({
    name: '',
    description: '',
    features: '{}',
  });
  const [featureSets, setFeatureSets] = useState<PlatformFeatureSet[]>([]);
  const [billingModeForm, setBillingModeForm] = useState({
    company_id: '',
    provider: 'self-managed' as 'self-managed' | 'rollops',
    require_billing_setup: false,
    enable_billing: true,
  });
  const [billingDefaultEnabled, setBillingDefaultEnabled] = useState(false);
  const [automatedBillingSignupEnabled, setAutomatedBillingSignupEnabled] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Note: AdminRoute component handles authorization check
  // This hook is for displaying admin user info, not for access control

  const loadSubscriptions = useCallback(async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('saas_subscriptions')
        .select(`
          *,
          companies!inner (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (companyIdFilter) {
        query = query.eq('company_id', companyIdFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const subs: SaaSSubscription[] = (data || []).map((sub) => {
        const typedSub = sub as SubscriptionWithCompany;
        return {
        id: typedSub.id,
        company_id: typedSub.company_id,
        company_name: typedSub.companies?.name || 'Unknown Company',
        stripe_subscription_id: typedSub.stripe_subscription_id,
        stripe_customer_id: typedSub.stripe_customer_id,
        plan_type: typedSub.plan_type as 'basic' | 'pro' | 'enterprise',
        status: typedSub.status,
        billing_cycle: typedSub.billing_cycle as 'monthly' | 'annual',
        price: typedSub.price,
        discount_percent: typedSub.discount_percent,
        discount_amount: typedSub.discount_amount,
        billing_start_date: typedSub.billing_start_date,
        billing_end_date: typedSub.billing_end_date,
        trial_end_date: typedSub.trial_end_date,
        delayed_billing_months: typedSub.delayed_billing_months,
        created_at: typedSub.created_at,
        updated_at: typedSub.updated_at,
      };
      });

      setSubscriptions(subs);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [companyIdFilter]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: pricesData, error: priceError } = await (supabase.from as any)('platform_saas_prices')
        .select(`
          id, name, description, amount_cents, interval, stripe_price_id, stripe_product_id, is_active, created_at,
          platform_price_coupons (
            platform_coupons (
              id, name, code, coupon_type, percent_off, amount_off_cents, free_months, stripe_coupon_id
            )
          ),
          platform_saas_price_rules (
            id, metric, amount_cents, interval, max_members, max_locations, description, is_active
          ),
          platform_price_feature_sets (
            platform_feature_sets (
              id, name, description, features
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (priceError) throw priceError;

      type PriceRow = {
        id: string;
        name: string;
        description: string | null;
        amount_cents: number;
        interval: 'month' | 'year';
        stripe_price_id: string | null;
        stripe_product_id: string | null;
        is_active: boolean;
        platform_price_coupons?: Array<{ platform_coupons: PlatformCoupon | null }> | null;
        platform_saas_price_rules?: PlatformPriceRule[] | null;
        platform_price_feature_sets?: Array<{ platform_feature_sets: PlatformFeatureSet | null }> | null;
      };

      const mappedPrices: PlatformPrice[] =
        (pricesData as PriceRow[] | null)?.map((row) => ({
          id: row.id,
          name: row.name,
          description: row.description,
          amount_cents: row.amount_cents,
          interval: row.interval,
          stripe_price_id: row.stripe_price_id,
          stripe_product_id: row.stripe_product_id,
          is_active: row.is_active,
          coupons:
            row.platform_price_coupons?.map((pc) => pc.platform_coupons as PlatformCoupon).filter((c): c is PlatformCoupon => c !== null) || [],
          rules: (row.platform_saas_price_rules || []) as PlatformPriceRule[],
          feature_sets:
            row.platform_price_feature_sets?.map((pf) => pf.platform_feature_sets as PlatformFeatureSet).filter((fs): fs is PlatformFeatureSet => fs !== null) || [],
        })) || [];

      setPlatformPrices(mappedPrices);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: couponsData, error: couponError } = await (supabase.from as any)('platform_coupons')
        .select('id, name, code, coupon_type, percent_off, amount_off_cents, free_months, stripe_coupon_id, is_active')
        .order('created_at', { ascending: false });

      if (couponError) throw couponError;

      setPlatformCoupons((couponsData || []) as unknown as PlatformCoupon[]);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: featureSetData, error: featureError } = await (supabase.from as any)('platform_feature_sets')
        .select('id, name, description, features, is_active')
        .order('created_at', { ascending: false });

      if (featureError) throw featureError;
      setFeatureSets((featureSetData || []) as unknown as PlatformFeatureSet[]);
    } catch {
      // Error loading platform catalog
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const loadPlatformSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      // Load billing_default_enabled
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: billingDefaultData, error: billingDefaultError } = await (supabase.from as any)('platform_settings')
        .select('value')
        .eq('key', 'billing_default_enabled')
        .maybeSingle();

      if (billingDefaultError) throw billingDefaultError;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setBillingDefaultEnabled(Boolean((billingDefaultData as any)?.value?.enabled));

      // Load automated_billing_signup_enabled
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: automatedData, error: automatedError } = await (supabase.from as any)('platform_settings')
        .select('value')
        .eq('key', 'automated_billing_signup_enabled')
        .maybeSingle();

      if (automatedError) throw automatedError;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setAutomatedBillingSignupEnabled(Boolean((automatedData as any)?.value?.enabled));
    } catch {
      // Error loading platform settings
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminUser) {
      loadSubscriptions();
      loadCatalog();
      loadPlatformSettings();
    }
  }, [adminUser, loadSubscriptions, loadCatalog, loadPlatformSettings]);

  const handleCreateSubscription = async () => {
    try {
      // Call edge function to create subscription
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/manage-saas-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create',
          ...formData,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create subscription');
      }

      await loadSubscriptions();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert(error instanceof Error ? error.message : 'Failed to create subscription');
    }
  };

  const handleUpdateSubscription = async (subscriptionId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/manage-saas-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'update',
          subscription_id: subscriptionId,
          ...formData,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update subscription');
      }

      await loadSubscriptions();
      setEditingSubscription(null);
      resetForm();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert(error instanceof Error ? error.message : 'Failed to update subscription');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: companyIdFilter || '',
      plan_type: 'basic',
      billing_cycle: 'monthly',
      price: 20,
      discount_percent: 0,
      discount_amount: 0,
      delayed_billing_months: 0,
      billing_start_date: new Date().toISOString().split('T')[0],
    });
  };

  const createPlatformPrice = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/manage-platform-catalog`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create_price',
          payload: priceForm,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create platform price');
      }

      setPriceForm({ name: '', amount_cents: 2000, interval: 'month', description: '' });
      await loadCatalog();
    } catch (error) {
      console.error('Error creating platform price:', error);
      alert(error instanceof Error ? error.message : 'Failed to create platform price');
    }
  };

  const createPlatformCoupon = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/manage-platform-catalog`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create_coupon',
          payload: couponForm,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create coupon');
      }

      setCouponForm({
        name: '',
        code: '',
        coupon_type: 'free_months',
        percent_off: 0,
        amount_off_cents: 0,
        free_months: 1,
        platform_price_id: '',
      });
      await loadCatalog();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create platform coupon');
    }
  };

  const createPlatformPriceRule = async () => {
    try {
      if (!ruleForm.platform_price_id) throw new Error('Platform price is required');

      const payload = {
        platform_price_id: ruleForm.platform_price_id,
        metric: ruleForm.metric,
        amount_cents: ruleForm.amount_cents,
        interval: ruleForm.interval,
        max_members: ruleForm.max_members || null,
        max_locations: ruleForm.max_locations || null,
        description: ruleForm.description || null,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('platform_saas_price_rules')
        .insert(payload);

      if (error) throw error;

      setRuleForm({
        platform_price_id: '',
        metric: 'per_location',
        amount_cents: 2000,
        interval: 'month',
        max_members: 0,
        max_locations: 0,
        description: '',
      });

      await loadCatalog();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create price rule');
    }
  };

  const createFeatureSet = async () => {
    try {
      const parsedFeatures = featureSetForm.features ? JSON.parse(featureSetForm.features) : {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('platform_feature_sets')
        .insert({
          name: featureSetForm.name,
          description: featureSetForm.description || null,
          features: parsedFeatures,
        });

      if (error) throw error;

      setFeatureSetForm({ name: '', description: '', features: '{}' });
      await loadCatalog();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create feature set (ensure valid JSON)');
    }
  };

  const attachFeatureSetToPrice = async (platformPriceId: string, featureSetId: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('platform_price_feature_sets')
        .insert({
          platform_price_id: platformPriceId,
          feature_set_id: featureSetId,
        });

      if (error) throw error;

      await loadCatalog();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to attach feature set');
    }
  };

  const attachCouponToPrice = async (platformPriceId: string, platformCouponId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/manage-platform-catalog`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'attach_coupon',
          payload: { platform_price_id: platformPriceId, platform_coupon_id: platformCouponId },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to attach coupon');
      }

      await loadCatalog();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to attach coupon');
    }
  };

  const createOffer = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('gym_price_offers').insert({
        company_id: offerForm.company_id,
        platform_price_id: offerForm.platform_price_id,
        notes: offerForm.notes || null,
      });

      if (error) throw error;

      const companyId = offerForm.company_id;
      const platformPriceId = offerForm.platform_price_id;
      const notes = offerForm.notes;
      
      setOfferForm({ company_id: '', platform_price_id: '', notes: '' });
      await logBillingEvent(companyId, 'PRICE_OFFER_CREATED', {
        platform_price_id: platformPriceId,
        notes,
      });
      alert('Offer created');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create offer');
    }
  };

  const logBillingEvent = async (companyId: string, eventType: string, details: Record<string, unknown>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase.from as any)('billing_events').insert({
        company_id: companyId || null,
        event_type: eventType,
        details,
        created_by: adminUser?.id || null,
      });
    } catch {
      // Failed to log billing event - non-critical
    }
  };

  const updateBillingDefault = async (nextEnabled: boolean) => {
    try {
      setSettingsLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('platform_settings')
        .upsert(
          {
            key: 'billing_default_enabled',
            value: { enabled: nextEnabled },
          },
          { onConflict: 'key' },
        );

      if (error) throw error;
      setBillingDefaultEnabled(nextEnabled);
      alert('Default billing toggle updated');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to update default billing toggle');
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateAutomatedBillingSignup = async (nextEnabled: boolean) => {
    try {
      setSettingsLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('platform_settings')
        .upsert(
          {
            key: 'automated_billing_signup_enabled',
            value: { enabled: nextEnabled },
          },
          { onConflict: 'key' },
        );

      if (error) throw error;
      setAutomatedBillingSignupEnabled(nextEnabled);
      alert('Automated billing signup toggle updated');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to update automated billing signup toggle');
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateBillingMode = async () => {
    try {
      if (!billingModeForm.company_id) throw new Error('Company ID required');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: upsertError } = await (supabase.from as any)('subscription_billing_settings')
        .upsert({
          company_id: billingModeForm.company_id,
          payment_provider: billingModeForm.provider,
        }, { onConflict: 'company_id' });

      if (upsertError) throw upsertError;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: companyUpdateError } = await (supabase.from as any)('companies')
        .update({
          enable_billing: billingModeForm.enable_billing,
          require_billing_setup: billingModeForm.require_billing_setup,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)
        .eq('id', billingModeForm.company_id);

      if (companyUpdateError) throw companyUpdateError;

      await logBillingEvent(billingModeForm.company_id, 'BILLING_MODE_UPDATED', {
        provider: billingModeForm.provider,
        require_billing_setup: billingModeForm.require_billing_setup,
        enable_billing: billingModeForm.enable_billing,
      });

      alert('Billing mode updated');
    } catch (error) {
      console.error('Error updating billing mode:', error);
      alert(error instanceof Error ? error.message : 'Failed to update billing mode');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      active: 'bg-green-500',
      trialing: 'bg-[#00D9FF]',
      past_due: 'bg-yellow-500',
      cancelled: 'bg-gray-500',
      unpaid: 'bg-red-500',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${statusColors[status] || 'bg-gray-500'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (authLoading || isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
        </div>
      </AdminLayout>
    );
  }

  if (!adminUser) {
    return null; // Will redirect
  }

  return (
    <AdminRoute>
      <AdminLayout>
        <div className="space-y-6 min-h-0">
        <div className="mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#EBFFEE] mb-1 sm:mb-2">RollOps Subscription Management</h1>
            <p className="text-xs sm:text-sm md:text-base text-[#A9A3A3]">Manage SaaS subscriptions for gyms (free months, contract terms, pricing)</p>
          </div>
          <Button
            variant="red"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Subscription
          </Button>
        </div>

        {/* Platform defaults */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Platform Defaults</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[#EBFFEE] font-semibold">Billing enabled by default for new companies</div>
                <div className="text-xs text-[#A9A3A3]">Controls default `enable_billing` on company creation. Leave off to onboard gyms intentionally.</div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#EBFFEE]">
                <input
                  type="checkbox"
                  checked={billingDefaultEnabled}
                  onChange={(e) => updateBillingDefault(e.target.checked)}
                  disabled={settingsLoading}
                />
                <span>{billingDefaultEnabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[#EBFFEE] font-semibold">Automated Billing Signup</div>
                <div className="text-xs text-[#A9A3A3]">When enabled, paid subscribers can automatically enable billing without admin approval. When disabled, all billing enablement requires manual admin approval.</div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#EBFFEE]">
                <input
                  type="checkbox"
                  checked={automatedBillingSignupEnabled}
                  onChange={(e) => updateAutomatedBillingSignup(e.target.checked)}
                  disabled={settingsLoading}
                />
                <span>{automatedBillingSignupEnabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
            {settingsLoading && <div className="text-xs text-[#A9A3A3]">Saving/loading…</div>}
          </CardContent>
        </Card>

        {/* Platform Price Catalog */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Platform Prices & Coupons</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[#EBFFEE] font-semibold">Create Price</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPriceForm({ name: 'Standard - $20/mo', amount_cents: 2000, interval: 'month', description: 'Standard SaaS tier' })}
                    >
                      $20/mo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPriceForm({ name: 'Pro - $40/mo', amount_cents: 4000, interval: 'month', description: 'Pro SaaS tier' })}
                    >
                      $40/mo
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Name</label>
                    <input
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={priceForm.name}
                      onChange={(e) => setPriceForm({ ...priceForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Amount (cents)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={priceForm.amount_cents}
                      onChange={(e) => setPriceForm({ ...priceForm, amount_cents: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Interval</label>
                    <select
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={priceForm.interval}
                      onChange={(e) => setPriceForm({ ...priceForm, interval: e.target.value as 'month' | 'year' })}
                    >
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Description</label>
                    <input
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={priceForm.description}
                      onChange={(e) => setPriceForm({ ...priceForm, description: e.target.value })}
                    />
                  </div>
                </div>
                <Button variant="red" onClick={createPlatformPrice} className="w-full sm:w-auto">
                  Save Price
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[#EBFFEE] font-semibold">Create Coupon</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCouponForm({
                        name: '2 Free Months',
                        code: '2FREE',
                        coupon_type: 'free_months',
                        percent_off: 0,
                        amount_off_cents: 0,
                        free_months: 2,
                        platform_price_id: '',
                      })}
                    >
                      2 Free Mo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCouponForm({
                        name: '1 Free Month',
                        code: '1FREE',
                        coupon_type: 'free_months',
                        percent_off: 0,
                        amount_off_cents: 0,
                        free_months: 1,
                        platform_price_id: '',
                      })}
                    >
                      1 Free Mo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCouponForm({
                        name: 'Referral $100',
                        code: 'REFERRAL100',
                        coupon_type: 'referral_credit',
                        percent_off: 0,
                        amount_off_cents: 10000,
                        free_months: 0,
                        platform_price_id: '',
                      })}
                    >
                      Referral $100
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Name</label>
                    <input
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.name}
                      onChange={(e) => setCouponForm({ ...couponForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Code</label>
                    <input
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.code}
                      onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Type</label>
                    <select
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.coupon_type}
                      onChange={(e) => setCouponForm({ ...couponForm, coupon_type: e.target.value as PlatformCoupon['coupon_type'] })}
                    >
                      <option value="free_months">Free Months</option>
                      <option value="percent">Percent Off</option>
                      <option value="amount">Amount Off</option>
                      <option value="referral_credit">Referral Credit</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Percent</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.percent_off}
                      onChange={(e) => setCouponForm({ ...couponForm, percent_off: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Amount (cents)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.amount_off_cents}
                      onChange={(e) => setCouponForm({ ...couponForm, amount_off_cents: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Free Months</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.free_months}
                      onChange={(e) => setCouponForm({ ...couponForm, free_months: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Attach to Price (optional)</label>
                    <select
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={couponForm.platform_price_id}
                      onChange={(e) => setCouponForm({ ...couponForm, platform_price_id: e.target.value })}
                    >
                      <option value="">None</option>
                      {platformPrices.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <Button variant="red" onClick={createPlatformCoupon} className="w-full sm:w-auto">
                  Save Coupon
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[#EBFFEE] font-semibold">Create Pricing Rule</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Metric</label>
                    <select
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={ruleForm.metric}
                      onChange={(e) => setRuleForm({ ...ruleForm, metric: e.target.value as PlatformPriceRule['metric'] })}
                    >
                      <option value="per_location">Per Location</option>
                      <option value="per_member">Per Member</option>
                      <option value="flat">Flat</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Amount (cents)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={ruleForm.amount_cents}
                      onChange={(e) => setRuleForm({ ...ruleForm, amount_cents: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Interval</label>
                    <select
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={ruleForm.interval}
                      onChange={(e) => setRuleForm({ ...ruleForm, interval: e.target.value as 'month' | 'year' })}
                    >
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Max Members (optional)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={ruleForm.max_members}
                      onChange={(e) => setRuleForm({ ...ruleForm, max_members: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Max Locations (optional)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={ruleForm.max_locations}
                      onChange={(e) => setRuleForm({ ...ruleForm, max_locations: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#EBFFEE] mb-1">Description</label>
                  <input
                    className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    value={ruleForm.description}
                    onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#EBFFEE] mb-1">Platform Price</label>
                  <select
                    className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    value={ruleForm.platform_price_id}
                    onChange={(e) => setRuleForm({ ...ruleForm, platform_price_id: e.target.value })}
                  >
                    <option value="">Select price</option>
                    {platformPrices.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (${(p.amount_cents / 100).toFixed(2)}/{p.interval === 'year' ? 'yr' : 'mo'})
                      </option>
                    ))}
                  </select>
                </div>
                <Button variant="red" onClick={createPlatformPriceRule} className="w-full sm:w-auto">
                  Save Rule
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[#EBFFEE] font-semibold">Create Feature Set</h3>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Name</label>
                    <input
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={featureSetForm.name}
                      onChange={(e) => setFeatureSetForm({ ...featureSetForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Description</label>
                    <input
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                      value={featureSetForm.description}
                      onChange={(e) => setFeatureSetForm({ ...featureSetForm, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#EBFFEE] mb-1">Features JSON</label>
                    <textarea
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] min-h-[96px]"
                      value={featureSetForm.features}
                      onChange={(e) => setFeatureSetForm({ ...featureSetForm, features: e.target.value })}
                    />
                    <div className="text-xs text-[#A9A3A3] mt-1">Example: {"{\"timecard\":true,\"payroll\":true}"}</div>
                  </div>
                </div>
                <Button variant="red" onClick={createFeatureSet} className="w-full sm:w-auto">
                  Save Feature Set
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-[#EBFFEE] font-semibold mb-2">Catalog</h3>
              {catalogLoading ? (
                <div className="text-[#A9A3A3]">Loading...</div>
              ) : platformPrices.length === 0 ? (
                <div className="text-[#A9A3A3]">No platform prices yet</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {platformPrices.map((price) => (
                    <div key={price.id} className="border border-[#504A4A] rounded-lg p-4 bg-[#1A1818]">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-[#EBFFEE] font-semibold">{price.name}</div>
                          <div className="text-sm text-[#A9A3A3]">{price.description || '—'}</div>
                        </div>
                        <div className="text-[#EBFFEE] font-bold">
                          ${(price.amount_cents / 100).toFixed(2)}/{price.interval === 'year' ? 'yr' : 'mo'}
                        </div>
                      </div>
                      <div className="text-xs text-[#A9A3A3] mt-1">
                        Stripe Price: {price.stripe_price_id || 'pending'}
                      </div>
                      <div className="mt-3 border-t border-[#504A4A] pt-3">
                        <div className="text-xs text-[#EBFFEE] font-semibold mb-1">Rules</div>
                        {price.rules.length === 0 ? (
                          <div className="text-xs text-[#A9A3A3]">None</div>
                        ) : (
                          <div className="space-y-1">
                            {price.rules.map((r) => (
                              <div key={r.id} className="text-xs text-[#EBFFEE] flex justify-between gap-2">
                                <span>
                                  {r.metric === 'flat' ? 'Flat' : r.metric === 'per_location' ? 'Per location' : 'Per member'} · ${ (r.amount_cents / 100).toFixed(2) }/{r.interval === 'year' ? 'yr' : 'mo'}
                                  {r.max_members ? ` · up to ${r.max_members} members` : ''}
                                  {r.max_locations ? ` · up to ${r.max_locations} locations` : ''}
                                </span>
                                {!r.is_active && <span className="text-[#A9A3A3]">(inactive)</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 border-t border-[#504A4A] pt-3">
                        <div className="text-xs text-[#EBFFEE] font-semibold mb-1">Feature Sets</div>
                        {price.feature_sets.length === 0 ? (
                          <div className="text-xs text-[#A9A3A3]">None</div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {price.feature_sets.map((f) => (
                              <span key={f.id} className="text-xs bg-[#363232] text-[#EBFFEE] px-2 py-1 rounded">
                                {f.name}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2">
                          <select
                            className="flex-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                            onChange={(e) => {
                              const featureId = e.target.value;
                              if (featureId) attachFeatureSetToPrice(price.id, featureId);
                            }}
                          >
                            <option value="">Attach feature set…</option>
                            {featureSets.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="text-xs text-[#EBFFEE] font-semibold mb-1">Coupons</div>
                        {price.coupons.length === 0 ? (
                          <div className="text-xs text-[#A9A3A3]">None</div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {price.coupons.map((c) => (
                              <span key={c.id} className="text-xs bg-[#363232] text-[#EBFFEE] px-2 py-1 rounded">
                                {c.code} ({c.coupon_type})
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2">
                          <select
                            className="flex-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                            onChange={(e) => {
                              const couponId = e.target.value;
                              if (couponId) attachCouponToPrice(price.id, couponId);
                            }}
                          >
                            <option value="">Attach coupon…</option>
                            {platformCoupons.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.code} ({c.coupon_type})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Gym Offer + Billing Mode Controls */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Offers & Billing Modes</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="text-[#EBFFEE] font-semibold">Invite Gym to Price</h3>
              <div>
                <label className="block text-sm text-[#EBFFEE] mb-1">Company ID</label>
                <input
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                  value={offerForm.company_id}
                  onChange={(e) => setOfferForm({ ...offerForm, company_id: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-[#EBFFEE] mb-1">Platform Price</label>
                <select
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                  value={offerForm.platform_price_id}
                  onChange={(e) => setOfferForm({ ...offerForm, platform_price_id: e.target.value })}
                >
                  <option value="">Select price</option>
                  {platformPrices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (${(p.amount_cents / 100).toFixed(2)}/{p.interval === 'year' ? 'yr' : 'mo'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#EBFFEE] mb-1">Notes</label>
                <textarea
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                  value={offerForm.notes}
                  onChange={(e) => setOfferForm({ ...offerForm, notes: e.target.value })}
                />
              </div>
              <Button variant="red" onClick={createOffer} className="w-full sm:w-auto">
                Send Offer
              </Button>
            </div>

            <div className="space-y-3">
              <h3 className="text-[#EBFFEE] font-semibold">Billing Mode (Self / RollOps)</h3>
              <div>
                <label className="block text-sm text-[#EBFFEE] mb-1">Company ID</label>
                <input
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                  value={billingModeForm.company_id}
                  onChange={(e) => setBillingModeForm({ ...billingModeForm, company_id: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-[#EBFFEE] mb-1">Provider</label>
                <select
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                  value={billingModeForm.provider}
                  onChange={(e) => setBillingModeForm({ ...billingModeForm, provider: e.target.value as 'self-managed' | 'rollops' })}
                >
                  <option value="self-managed">Self Managed</option>
                  <option value="rollops">RollOps Managed</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={billingModeForm.require_billing_setup}
                  onChange={(e) => setBillingModeForm({ ...billingModeForm, require_billing_setup: e.target.checked })}
                />
                <span className="text-sm text-[#EBFFEE]">Require billing setup (make mandatory)</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={billingModeForm.enable_billing}
                  onChange={(e) => setBillingModeForm({ ...billingModeForm, enable_billing: e.target.checked })}
                />
                <span className="text-sm text-[#EBFFEE]">Enable billing features</span>
              </div>
              <Button variant="red" onClick={updateBillingMode} className="w-full sm:w-auto">
                Update Billing Mode
              </Button>
              <p className="text-xs text-[#A9A3A3]">
                Select the billing provider for this gym. Self-managed means the gym handles payments directly.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Subscriptions List */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Subscriptions ({subscriptions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {subscriptions.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="mx-auto h-12 w-12 text-[#A9A3A3] mb-4" />
                <p className="text-[#A9A3A3]">No subscriptions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#504A4A]">
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Company</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Plan</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Price</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Billing Cycle</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Free Months</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Discount</th>
                      <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="border-b border-[#504A4A] hover:bg-[#363232]">
                        <td className="py-4 px-4">
                          <div className="font-medium text-[#EBFFEE]">{sub.company_name}</div>
                          <div className="text-sm text-[#A9A3A3]">{sub.company_id.slice(0, 8)}...</div>
                        </td>
                        <td className="py-4 px-4 text-[#EBFFEE]">
                          {sub.plan_type.charAt(0).toUpperCase() + sub.plan_type.slice(1)}
                        </td>
                        <td className="py-4 px-4">
                          {getStatusBadge(sub.status)}
                        </td>
                        <td className="py-4 px-4 text-[#EBFFEE]">
                          ${sub.price.toFixed(2)}/{sub.billing_cycle === 'annual' ? 'yr' : 'mo'}
                        </td>
                        <td className="py-4 px-4 text-[#EBFFEE]">
                          {sub.billing_cycle.charAt(0).toUpperCase() + sub.billing_cycle.slice(1)}
                        </td>
                        <td className="py-4 px-4 text-[#EBFFEE]">
                          {sub.delayed_billing_months > 0 ? `${sub.delayed_billing_months} months` : 'None'}
                        </td>
                        <td className="py-4 px-4 text-[#EBFFEE]">
                          {sub.discount_percent > 0 ? `${sub.discount_percent}%` : 
                           sub.discount_amount > 0 ? `$${sub.discount_amount.toFixed(2)}` : 'None'}
                        </td>
                        <td className="py-4 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFormData({
                                company_id: sub.company_id,
                                plan_type: sub.plan_type,
                                billing_cycle: sub.billing_cycle,
                                price: sub.price,
                                discount_percent: sub.discount_percent,
                                discount_amount: sub.discount_amount,
                                delayed_billing_months: sub.delayed_billing_months,
                                billing_start_date: sub.billing_start_date,
                              });
                              setEditingSubscription(sub);
                            }}
                            className="text-rollops-accent hover:text-[#EBFFEE]"
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        {(showCreateModal || editingSubscription) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="bg-[#221F1F] border-[#504A4A] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-[#EBFFEE]">
                    {editingSubscription ? 'Edit Subscription' : 'Create Subscription'}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingSubscription(null);
                      resetForm();
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Company ID</label>
                  <input
                    type="text"
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                    disabled={!!editingSubscription}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Plan Type</label>
                    <select
                      value={formData.plan_type}
                      onChange={(e) => setFormData({ ...formData, plan_type: e.target.value as 'basic' | 'pro' | 'enterprise' })}
                      className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                    >
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Billing Cycle</label>
                    <select
                      value={formData.billing_cycle}
                      onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value as 'monthly' | 'annual' })}
                      className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Discount (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Discount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discount_amount}
                      onChange={(e) => setFormData({ ...formData, discount_amount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Free Months (Delayed Billing)</label>
                  <input
                    type="number"
                    value={formData.delayed_billing_months}
                    onChange={(e) => setFormData({ ...formData, delayed_billing_months: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                  />
                  <p className="text-sm text-[#A9A3A3] mt-1">Number of months to delay billing (e.g., 2-3 months free)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Billing Start Date</label>
                  <input
                    type="date"
                    value={formData.billing_start_date}
                    onChange={(e) => setFormData({ ...formData, billing_start_date: e.target.value })}
                    className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE]"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingSubscription(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="red"
                    onClick={() => {
                      if (editingSubscription) {
                        handleUpdateSubscription(editingSubscription.id);
                      } else {
                        handleCreateSubscription();
                      }
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingSubscription ? 'Update' : 'Create'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      </AdminLayout>
    </AdminRoute>
  );
}

